import { useCallback } from 'react'

import { Link } from '@inertiajs/react'

import { useTabStyles } from './hooks/useTabStyles'
import { useFeatures } from 'hooks/useFeatures'

import { normalizePath } from './helpers'
import type { NavbarLinkProps } from './types'

export type NavbarProps = {
  links: NavbarLinkProps[]
  'aria-label': string
  classes?: Partial<Record<'root' | 'list' | 'listItem' | 'link', string>>
}

export const Navbar = ({ links, 'aria-label': ariaLabel, classes }: NavbarProps) => {
  const currentPath = typeof window !== 'undefined' ? normalizePath(window.location.pathname) : ''
  const currentSearch = typeof window !== 'undefined' ? window.location.search : ''

  const features = useFeatures()

  const availableLinks = links.filter((t) => {
    if (!t.featureFlag) return true
    return features[t.featureFlag] === true
  })

  // Most tabs are distinct paths, so matching on pathname alone is enough. Some tabs instead share
  // one path and are differentiated only by query params (e.g. a wizard's `?step=`) - for those,
  // match on path AND every param in the tab's href. Extra params in the current URL (e.g. a
  // DataGrid's page/sort/filter state) are ignored, so the tab stays active as the grid updates.
  const matchesHref = (href: string) => {
    const [hrefPath, hrefQuery] = href.split('?')
    const target = normalizePath(hrefPath)

    if (hrefQuery) {
      const currentParams = new URLSearchParams(currentSearch)
      return (
        currentPath === target &&
        [...new URLSearchParams(hrefQuery).entries()].every(([key, value]) => currentParams.get(key) === value)
      )
    }

    return currentPath === target || currentPath.startsWith(`${target}/`)
  }

  const currentPage = availableLinks.find(({ href }) => {
    // href can be a string or an array of strings to ensure tab remains active across multiple paths
    return Array.isArray(href) ? href.some(matchesHref) : matchesHref(href)
  })

  // always ensure Tabs has a selected value.
  const activeTabValue: string | false = currentPage?.value ?? false

  const url = useCallback(
    (tabValue: string) => {
      const href = links.find(({ value }) => value === tabValue)?.href
      return Array.isArray(href) ? href[0] : href // Tabs should always navigate to first path in array
    },
    [links]
  )

  const handleNavigate = useCallback(
    (tabValue: string) => {
      const hrefToNavigateTo = url(tabValue)

      // Only navigate if we're not already at the target path
      if (hrefToNavigateTo && normalizePath(hrefToNavigateTo) !== currentPath) {
        window.location.replace(hrefToNavigateTo)
      }
    },
    [currentPath, url]
  )
  const { classes: internalClasses, cx } = useTabStyles()

  return (
    <nav aria-label={ariaLabel} className={cx(internalClasses.root, classes?.root)}>
      <ul className={cx(internalClasses.list, classes?.list)}>
        {availableLinks.map(({ value: tabValue, label, type = 'ruby' }) => {
          const isActive = activeTabValue === tabValue
          return (
            <li key={tabValue} className={cx(internalClasses.listItem, classes?.listItem, isActive && 'Mui-selected')}>
              {
                {
                  react: (
                    <Link
                      href={url(tabValue)}
                      className={cx(internalClasses.link, classes?.link)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {label}
                    </Link>
                  ),
                  ruby: (
                    <a
                      href={url(tabValue)}
                      className={cx(internalClasses.link, classes?.link)}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={(e) => {
                        e.preventDefault()
                        handleNavigate(tabValue)
                      }}
                    >
                      {label}
                    </a>
                  ),
                }[type]
              }
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
